import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import axios from 'axios';
import Api from '../../Api/Api.json';

const SkinDisease = () => {
  const navigate = useNavigate();
  const [photoImage, setPhotoImage] = useState(null);
  const [photoBase64, setPhotoBase64] = useState(null);
  const [skinResults, setSkinResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);

  const takeSkinPhoto = async () => {
    try {
      if (Capacitor.isNativePlatform()) {
        // Native platform: Use camera
        const photo = await Camera.getPhoto({
          quality: 60,
          width: 1024,
          height: 1024,
          source: CameraSource.Camera,
          resultType: CameraResultType.Base64
        });

        if (photo.base64String) {
          const imageUrl = `data:image/jpeg;base64,${photo.base64String}`;
          setPhotoImage(imageUrl);
          setPhotoBase64(photo.base64String);
          setSkinResults(null);
          setErrorMessage(null);
          
          // Automatically call API after photo is taken
          await analyzeSkinProblem(photo.base64String);
        }
      } else {
        // Web platform: Use file input
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
          const file = e.target.files[0];
          if (!file) return;

          try {
            // Convert file to base64
            const base64String = await fileToBase64(file);
            const imageUrl = `data:image/jpeg;base64,${base64String}`;
            setPhotoImage(imageUrl);
            setPhotoBase64(base64String);
            setSkinResults(null);
            setErrorMessage(null);
            
            // Automatically call API after photo is selected
            await analyzeSkinProblem(base64String);
          } catch (error) {
            console.error('File conversion error:', error);
            alert('Failed to process the image. Please try again.');
          }
        };
        input.click();
      }
    } catch (error) {
      console.error('Camera Error:', error);
      if (error.message !== 'User cancelled photos app') {
        alert('Failed to take photo. Please try again.');
      }
    }
  };

  const fileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        // Remove data URL prefix (e.g., "data:image/jpeg;base64,")
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = (error) => reject(error);
      reader.readAsDataURL(file);
    });
  };

  const analyzeSkinProblem = async (base64String) => {
    setIsLoading(true);
    setErrorMessage(null);
    
    try {
      // Get user's timezone (e.g., 'Asia/Dhaka')
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Get user_id from localStorage
      const userInfo = localStorage.getItem('user_info') 
        ? JSON.parse(localStorage.getItem('user_info'))
        : null;
      const userId = userInfo?.di_id || null;
      
      const headers = { headers: { "Content-Type": "application/json" } };
      const url = Api.SKIN_DISEASE_API;
      const requestData = {
        task: "getSkinAbnormality",
        photo_str: base64String,
        language: "english",
        timezone: timezone,
        user_id: userId
      };
      console.log(requestData);
      const response = await axios.post(url, requestData, headers);
      
      // Handle different response structures
      let responseData = response.data;
      if (responseData?.body) responseData = responseData.body;
      if (responseData?.data) responseData = responseData.data;
      console.log(responseData);
      if (responseData?.status === "success" && responseData?.results) {
        // Store all response data including problem, severity, and results
        setSkinResults({
          problem: responseData.problem || '',
          severity: responseData.severity || '',
          results: responseData.results || ''
        });
        setShowResultsModal(true);
      } else if (responseData?.status === "failure") {
        setErrorMessage("Did not detect skin abnormality in the photo. Please try again with a clearer image.");
        setShowResultsModal(true);
      } else {
        setErrorMessage("Unable to analyze the photo. Please try again.");
        setShowResultsModal(true);
      }
    } catch (error) {
      console.error('API Error:', error);
      setErrorMessage("Failed to analyze photo. Please try again.");
      setShowResultsModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const closeResultsModal = () => {
    setShowResultsModal(false);
    setSkinResults(null);
    setErrorMessage(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-14">
      <div className="container mx-auto px-4 py-6">
        {/* Back Button */}
        <div className="mb-4">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-blue-600 transition-colors duration-200"
          >
            <svg
              className="w-5 h-5 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            <span className="text-sm font-medium">ফিরে যান</span>
          </button>
        </div>
        
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-2">চর্মরোগ</h1>
        </div>

        {/* Information Text */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-gray-700">
            একটি পরিষ্কার ছবি তুলুন এবং সমস্যা সম্পর্কিত তথ্য পান। এটি আপনার তথ্যের জন্য, চিকিৎসা পরামর্শ নয়। চিকিৎসা নির্ণয়ের জন্য একজন ডাক্তারের সাথে পরামর্শ করুন।
          </p>
        </div>

        {/* Take Photo Button */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <button
            onClick={takeSkinPhoto}
            disabled={isLoading}
            className="w-full py-3 px-4 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span>Analyzing...</span>
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
                <span>{Capacitor.isNativePlatform() ? 'Take Skin Photo' : 'Upload Skin Photo'}</span>
              </>
            )}
          </button>
        </div>

        {/* Skin Analysis Results Modal */}
        {showResultsModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">Skin Analysis Results</h2>
                <button
                  onClick={closeResultsModal}
                  className="text-gray-500 hover:text-gray-700 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Modal Content */}
              <div className="p-6">
                {errorMessage ? (
                  <div className="text-center py-8">
                    <div className="text-red-600 text-lg font-medium mb-2">{errorMessage}</div>
                    {photoImage && (
                      <img
                        src={photoImage}
                        alt="Captured skin"
                        className="max-w-xs mx-auto rounded-lg shadow-md mt-4"
                      />
                    )}
                  </div>
                ) : skinResults ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Image on Left */}
                    <div className="flex items-center justify-center">
                      {photoImage && (
                        <img
                          src={photoImage}
                          alt="Captured skin"
                          className="max-w-full h-auto rounded-lg shadow-md"
                        />
                      )}
                    </div>

                    {/* Results on Right */}
                    <div className="flex flex-col">
                      <h3 className="text-xl font-semibold text-gray-800 mb-4">Analysis Results</h3>
                      <div className="bg-gray-50 rounded-lg p-4 space-y-4">
                        {skinResults.problem && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800 mb-2">Problem:</h4>
                            <p className="text-sm text-gray-700">{skinResults.problem}</p>
                          </div>
                        )}
                        {skinResults.severity && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800 mb-2">Severity:</h4>
                            <p className="text-sm text-gray-700">{skinResults.severity}</p>
                          </div>
                        )}
                        {skinResults.results && (
                          <div>
                            <h4 className="text-sm font-semibold text-gray-800 mb-2">Details:</h4>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap">
                              {skinResults.results}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="text-gray-600">No results to display</div>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="sticky bottom-0 bg-gray-50 border-t border-gray-200 px-6 py-4 flex justify-end">
                <button
                  onClick={closeResultsModal}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SkinDisease;

